import { useCallback, useEffect, useRef, useState, type CSSProperties, type ReactNode, type KeyboardEvent as ReactKeyboardEvent, type PointerEvent as ReactPointerEvent } from 'react';
import { cn } from '@/lib/utils';
const PANEL_MIN_WIDTH_PX = 380;
const PANEL_MAX_VIEWPORT_RATIO = 0.5;
const PANEL_WIDTH_STORAGE_KEY = 'domino:app-chat-panel-width';
const getPanelMaxWidth = (): number => {
  if (typeof window === 'undefined') return PANEL_MIN_WIDTH_PX;
  return Math.max(
    PANEL_MIN_WIDTH_PX,
    Math.floor(window.innerWidth * PANEL_MAX_VIEWPORT_RATIO)
  );
};

const clampPanelWidth = (width: number): number =>
  Math.min(Math.max(width, PANEL_MIN_WIDTH_PX), getPanelMaxWidth());

const getInitialPanelWidth = (): number => {
  if (typeof window === 'undefined') return PANEL_MIN_WIDTH_PX;

  try {
    const stored = window.localStorage.getItem(PANEL_WIDTH_STORAGE_KEY);
    const parsed = stored ? Number.parseInt(stored, 10) : Number.NaN;
    return clampPanelWidth(Number.isFinite(parsed) ? parsed : PANEL_MIN_WIDTH_PX);
  } catch {
    return PANEL_MIN_WIDTH_PX;
  }
};

/** `fullscreen` (22/10): the panel covers the whole window, without the resize handle. */
export const AssistantPanel = ({children, label, fullscreen = false}: {children: ReactNode; label: string; fullscreen?: boolean}) => {
  const [panelWidth, setPanelWidth] = useState(getInitialPanelWidth);
  const [isResizing, setIsResizing] = useState(false);
  const resizeStateRef = useRef<{ startX: number; startWidth: number } | null>(null);
  useEffect(() => {
    const handleWindowResize = () => {
      setPanelWidth((current) => clampPanelWidth(current));
    };

    window.addEventListener('resize', handleWindowResize);
    return () => window.removeEventListener('resize', handleWindowResize);
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        PANEL_WIDTH_STORAGE_KEY,
        String(Math.round(panelWidth))
      );
    } catch {
      // Ignore private browsing or locked storage; resizing should still work.
    }
  }, [panelWidth]);

  useEffect(() => {
    if (!isResizing) return;

    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;

    const handlePointerMove = (event: PointerEvent) => {
      const resizeState = resizeStateRef.current;
      if (!resizeState) return;

      setPanelWidth(
        clampPanelWidth(resizeState.startWidth + resizeState.startX - event.clientX)
      );
    };

    const stopResize = () => {
      resizeStateRef.current = null;
      setIsResizing(false);
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', stopResize);
    window.addEventListener('pointercancel', stopResize);

    return () => {
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', stopResize);
      window.removeEventListener('pointercancel', stopResize);
    };
  }, [isResizing]);

  const startResize = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (event.button !== 0) return;

      event.preventDefault();
      resizeStateRef.current = {
        startX: event.clientX,
        startWidth: panelWidth,
      };
      setIsResizing(true);
    },
    [panelWidth]
  );

  const handleResizeKeyDown = useCallback((event: ReactKeyboardEvent<HTMLDivElement>) => {
    const step = event.shiftKey ? 80 : 24;

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        setPanelWidth((current) => clampPanelWidth(current + step));
        break;
      case 'ArrowRight':
        event.preventDefault();
        setPanelWidth((current) => clampPanelWidth(current - step));
        break;
      case 'Home':
        event.preventDefault();
        setPanelWidth(PANEL_MIN_WIDTH_PX);
        break;
      case 'End':
        event.preventDefault();
        setPanelWidth(getPanelMaxWidth());
        break;
      default:
        break;
    }
  }, []);

  return (
    <aside aria-label={label}
      className={fullscreen
        ? 'fixed inset-0 z-50 flex min-h-0 w-full flex-col bg-domino-warm'
        : 'fixed inset-x-0 bottom-0 z-50 flex h-[min(88dvh,760px)] w-full min-h-0 shrink-0 flex-col rounded-t-3xl border-t border-domino-border bg-domino-warm shadow-[var(--domino-shadow-elevated)] md:inset-y-0 md:left-auto md:h-auto md:w-[var(--assistant-panel-width)] md:rounded-none md:border-l md:border-t-0 xl:relative xl:inset-auto'}
      style={{ '--assistant-panel-width': `${panelWidth}px` } as CSSProperties}
    >
      {!fullscreen && <div
        role="separator"
        aria-label="Redimensionner le chat Domino"
        aria-orientation="vertical"
        aria-valuemin={PANEL_MIN_WIDTH_PX}
        aria-valuemax={getPanelMaxWidth()}
        aria-valuenow={Math.round(panelWidth)}
        tabIndex={0}
        onPointerDown={startResize}
        onKeyDown={handleResizeKeyDown}
        className={cn(
          'group absolute inset-y-0 left-0 z-20 hidden w-3 -translate-x-1/2 cursor-col-resize touch-none outline-none md:block',
          'after:absolute after:inset-y-0 after:left-1/2 after:w-0.5 after:-translate-x-1/2 after:bg-transparent after:transition-colors',
          'hover:after:bg-domino-coral focus-visible:after:bg-domino-coral',
          isResizing && 'after:bg-domino-coral'
        )}
      />}
      {children}
    </aside>
  );
};
